import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ActivityIndicator, ScrollView, TouchableOpacity, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { RouteProp, useFocusEffect, useRoute } from '@react-navigation/native'
import { useNavigation } from '@react-navigation/core'
import { StackNavigationProp } from '@react-navigation/stack'
import { startCase } from 'lodash'
import { NavigationContainer } from '@screens/Common/containers'
import { Button, Icon, Text } from '@components/base'
import { StyleService, useStyleSheet } from '@style/service'
import {
  ChatbotConversation,
  HealthDataReport,
  HealthDataReportKind,
  HealthDataReportStateKind,
} from '@src/types'
import { usePollReport } from '@screens/Nora/hooks/usePollReport'
import { MarkdownView } from '@src/components/Markdown'
import { useGoBack } from '@src/utils/navigation'
import { formatReportDateRange, isReportUnread } from '@screens/Nora/utils'
import { AppStackParamList } from '@src/navigation/types'
import { useDispatchAsync } from '@utils'
import { sharePdf } from '@utils/pdf'
import { NavigatorContext, useSnack } from '@src/utils/navigatorContext'
import { NSLoadingIndicator } from '@components'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { useGetRequiredAiProcessingPolicy } from '@src/screens/PolicyConsents/hooks/useAiProcessingConsentGate'
import { useNoraStreamChat } from '../context/NoraStreamChatContext'
import { cleanupPdfFile, downloadReportPdf } from '../services/reportPdfService'

const useReportFromParams = () => {
  const route = useRoute<RouteProp<AppStackParamList, 'NoraReportScreen'>>()
  const dispatchAsync = useDispatchAsync()
  const [fetchedReport, setFetchedReport] = useState<HealthDataReport | null>(null)
  const [fetchLoading, setFetchLoading] = useState(false)

  const params = route.params

  const isDeepLinked = !('report' in params)

  useEffect(() => {
    if (!isDeepLinked) {
      return
    }

    const fetchReport = async () => {
      setFetchLoading(true)
      try {
        const report = await dispatchAsync<HealthDataReport>({
          type: 'nora/healthDataReport',
          payload: { id: (params as { reportId: string }).reportId },
        })
        setFetchedReport(report)
      } finally {
        setFetchLoading(false)
      }
    }

    fetchReport()
  }, [dispatchAsync, isDeepLinked, params])

  return {
    report: isDeepLinked ? fetchedReport : (params as { report: HealthDataReport }).report,
    fetchLoading,
  }
}

const readyStates = [HealthDataReportStateKind.Completed, HealthDataReportStateKind.PdfGenerated]

const reportTitle = (report: HealthDataReport | null) => {
  if (!report) {
    return 'Report'
  }

  if (
    report.kind === HealthDataReportKind.Custom ||
    report.kind === HealthDataReportKind.CustomBlocks
  ) {
    return 'My Health Report'
  }

  return startCase(report.kind)
}

export const NoraReportScreen = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const goBack = useGoBack()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const showSnack = useSnack()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const { openConversation, ensureAiProcessingConsent } = useNoraStreamChat()
  const openingConversationRef = useRef(false)

  const { report: initialReport, fetchLoading } = useReportFromParams()
  const { report, loading: pollLoading } = usePollReport({ report: initialReport })
  const [isDownloading, setIsDownloading] = useState(false)

  const state = report?.lastState.kind
  const subtitle = report ? formatReportDateRange(report) : undefined
  const isReady = state && readyStates.includes(state)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const loading = fetchLoading || !report || pollLoading || isRedirecting

  const handleDownloadPdf = useCallback(async () => {
    if (!report || isDownloading) {
      return
    }

    setIsDownloading(true)
    try {
      const generateResponse = await dispatchAsync<{ id: string; pdfUrl: string | null }>({
        type: 'nora/generateHealthDataReportPdf',
        payload: { id: report.id },
      })

      if (!generateResponse.pdfUrl) {
        showSnack('PDF generation failed', null, 'error')
        setIsDownloading(false)
        return
      }

      const result = await downloadReportPdf(report, generateResponse.pdfUrl)

      if (result.success) {
        try {
          await sharePdf(result.filePath, `${startCase(report.kind)} Report`)
        } catch {
          showSnack('Failed to share PDF', null, 'error')
        } finally {
          await cleanupPdfFile(result.filePath)
        }
      } else {
        showSnack(result.error, null, 'error')
      }
    } catch {
      showSnack('Failed to generate PDF', null, 'error')
    }

    setIsDownloading(false)
  }, [report, isDownloading, dispatchAsync, showSnack])

  // Resolves to null when AI processing consent is still required (the consent screen is opened) or
  // can't be verified, so callers never create or open a report conversation before consent.
  const getConversation = useCallback(
    async (report: HealthDataReport) => {
      if (!(await ensureAiProcessingConsent())) {
        return null
      }
      if (report.conversation && report.conversation.providerId) {
        return report.conversation
      }
      return dispatchAsync<ChatbotConversation>({
        type: 'nora/openChatbotReportConversation',
        payload: { reportId: report.id },
      })
    },
    [dispatchAsync, ensureAiProcessingConsent],
  )

  const handleReviewWithNora = useCallback(
    async (report: HealthDataReport) => {
      if (openingConversationRef.current) {
        return
      }

      openingConversationRef.current = true
      enableLoader()

      try {
        const conversation = await getConversation(report)
        if (conversation) {
          await openConversation(conversation)
        }
      } catch {
        showSnack('Failed to open report conversation', null, 'error')
      } finally {
        disableLoader()
        openingConversationRef.current = false
      }
    },
    [disableLoader, enableLoader, getConversation, openConversation, showSnack],
  )

  useEffect(() => {
    if (report && isReportUnread(report)) {
      dispatch({ type: 'nora/markHealthDataReportRead', payload: { id: report.id } })
    }
  }, [dispatch, report])

  const hasRedirectedRef = useRef(false)
  // Set when the redirect stopped for AI consent; on return, redirect only if consent was accepted
  // (re-prompting would trap a user who declined)
  const redirectBlockedByConsentRef = useRef(false)
  const [redirectAttempt, setRedirectAttempt] = useState(0)
  const { getRequiredAiProcessingPolicy } = useGetRequiredAiProcessingPolicy()

  useFocusEffect(
    useCallback(() => {
      if (!redirectBlockedByConsentRef.current) {
        return
      }

      redirectBlockedByConsentRef.current = false
      getRequiredAiProcessingPolicy()
        .then((policy) => {
          if (!policy) {
            setRedirectAttempt((attempt) => attempt + 1)
          }
        })
        .catch(() => undefined)
    }, [getRequiredAiProcessingPolicy]),
  )

  useEffect(() => {
    if (
      !report ||
      !isReady ||
      report.kind !== HealthDataReportKind.CustomBlocks ||
      hasRedirectedRef.current
    ) {
      return
    }

    hasRedirectedRef.current = true
    setIsRedirecting(true)

    const redirect = async () => {
      try {
        const conversation = await getConversation(report)
        if (!conversation) {
          hasRedirectedRef.current = false
          redirectBlockedByConsentRef.current = true
          setIsRedirecting(false)
          return
        }
        await openConversation(conversation, { replace: true })
      } catch {
        hasRedirectedRef.current = false
        setIsRedirecting(false)
        showSnack('Failed to open report conversation', null, 'error')
      }
    }

    redirect()
  }, [report, isReady, openConversation, getConversation, showSnack, redirectAttempt])

  const downloadButton = (
    <TouchableOpacity
      style={styles.downloadButton}
      accessibilityLabel="Download PDF"
      onPress={handleDownloadPdf}
      disabled={isDownloading}
    >
      {isDownloading ? (
        <ActivityIndicator size="small" />
      ) : (
        <Icon name="file-arrow-down" size="24" />
      )}
    </TouchableOpacity>
  )

  return (
    <NavigationContainer
      title={reportTitle(report)}
      subtitle={subtitle}
      goBack={goBack}
      rightAccessories={isReady ? [downloadButton] : undefined}
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={[
          styles.content,
          { paddingBottom: insets.bottom + (pollLoading ? 0 : 16), flexGrow: 1 },
        ]}
      >
        {pollLoading && (
          <View style={styles.loadingContainer}>
            <View style={styles.loadingContent}>
              <NSLoadingIndicator />
              <Text type="regular" style={styles.centered}>
                Your report will be ready in a few minutes. We'll notify you when it's done.
              </Text>
            </View>
            <Button type="outline" size="block" accessibilityLabel="Go Back" onPress={goBack}>
              Go Back
            </Button>
          </View>
        )}
        {!loading && state === HealthDataReportStateKind.Failed && (
          <Text type="regular" style={styles.centered}>
            Report generation failed
          </Text>
        )}
        {!loading && isReady && report.reportData && (
          <View style={styles.report}>
            <MarkdownView content={report.reportData} />
            <Button
              type="primary"
              size="block"
              accessibilityLabel="Review With Nora"
              onPress={() => handleReviewWithNora(report)}
            >
              Review With Nora
            </Button>
            <Button
              type="transparent"
              size="block"
              accessibilityLabel="Discuss With Your Coach"
              onPress={() => navigation.navigate('ScheduleNextCallModal')}
            >
              Discuss With Your Coach
            </Button>
          </View>
        )}
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    padding: 16,
    gap: 12,
  },
  content: {
    justifyContent: 'center',
  },
  loadingContainer: {
    flex: 1,
  },
  loadingContent: {
    paddingHorizontal: 40,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  loadingBar: {
    alignSelf: 'stretch',
  },
  centered: {
    textAlign: 'center',
  },
  report: {
    gap: 8,
  },
  downloadButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
})
